import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity, Compass, Handshake, Megaphone, IdCard, Users, Calendar, FileText, Mic,
  FolderOpen, Brain, MessageSquare, DollarSign, ShieldCheck, Building2, Inbox, Settings,
  Plus, Loader2, Trash2, Receipt, FileSpreadsheet, TrendingUp, AlertTriangle, Clock,
  Send, CheckCircle2, XCircle, Printer, CreditCard, Wallet, ShieldAlert, Sparkles,
  LayoutGrid,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';
import { CLAIM_STATUS_META, money, openSuperbill } from '../lib/rcm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const m = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.draft;
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`} data-testid={`claim-status-${status}`}>{m.label}</span>;
};

const Kpi = ({ icon: Icon, label, value, sub, accent = 'pine', testId }) => {
  const a = { pine: 'bg-cn-primary/10 text-cn-primary', terracotta: 'bg-cn-terracotta-50 text-cn-terracotta-600', amber: 'bg-amber-50 text-amber-600', sage: 'bg-cn-sage-100 text-cn-primary' }[accent];
  return (
    <div className="cn-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${a}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="mt-2 font-display font-bold text-2xl text-cn-text dark:text-white">{value}</div>
      {sub && <div className="text-xs text-cn-text-muted mt-0.5">{sub}</div>}
    </div>
  );
};

export default function RcmDashboard() {
  const { getAuthHeader } = useAuth();
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState([]);
  const [codeSets, setCodeSets] = useState({ cpt: [], icd10: [], place_of_service: [] });
  const [eligible, setEligible] = useState([]);
  const [unbilled, setUnbilled] = useState({ items: [], count: 0, estimated_total: 0 });
  const [codingNote, setCodingNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const [s, c, cs, eb, ub] = await Promise.all([
        axios.get(`${API}/rcm/summary`, getAuthHeader()),
        axios.get(`${API}/rcm/claims`, getAuthHeader()),
        axios.get(`${API}/rcm/code-sets`, getAuthHeader()),
        axios.get(`${API}/rcm/eligible-bookings`, getAuthHeader()),
        axios.get(`${API}/rcm/unbilled`, getAuthHeader()),
      ]);
      setSummary(s.data); setClaims(c.data || []); setCodeSets(cs.data); setEligible(eb.data || []);
      setUnbilled(ub.data || { items: [], count: 0, estimated_total: 0 });
    } catch (e) {
      toast.error('Could not load billing data');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { (async () => { await load(); })(); }, [load]);

  // One-click auto-code an unbilled visit note into a draft claim, then open it to review.
  const codeUnbilled = async (item) => {
    setCodingNote(item.note_id);
    try {
      const { data } = await axios.post(`${API}/clinical-notes/${item.note_id}/create-claim`, {}, { ...getAuthHeader(), timeout: 90000 });
      toast.success(`Draft claim ${data.claim?.claim_number} created — coded ${data.coding?.recommended_cpt}. Review & submit below.`);
      await load();
      if (data.claim) setDetail(data.claim);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create a claim from this note');
    } finally { setCodingNote(null); }
  };

  // Bulk auto-bill: turn every eligible unbilled note into a draft claim in one shot.
  // Backend hard-caps at RCM_BULK_AUTOBILL_CAP (default 25). All-or-nothing tolerant —
  // partial failures are surfaced in the toast and the inbox just refreshes.
  const [bulkBilling, setBulkBilling] = useState(false);
  const bulkBillAll = async () => {
    if (bulkBilling) return;
    const n = unbilled.count;
    if (!n) return;
    if (!window.confirm(`Auto-code and create draft claims for ${n} unbilled ${n === 1 ? 'visit' : 'visits'}? This may take up to a minute. All claims stay DRAFT until you submit them.`)) return;
    setBulkBilling(true);
    try {
      const { data } = await axios.post(`${API}/rcm/unbilled/create-all`, {}, { ...getAuthHeader(), timeout: 180000 });
      const c = data.created_count || 0;
      const f = data.failed_count || 0;
      const over = data.skipped_over_cap || 0;
      const total = data.total_charge || 0;
      let msg = `${c} draft ${c === 1 ? 'claim' : 'claims'} created · ${money(total)} ready to submit`;
      if (f > 0) msg += ` · ${f} skipped`;
      if (over > 0) msg += ` · ${over} over cap (re-run to continue)`;
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk auto-bill failed');
    } finally { setBulkBilling(false); }
  };

  const [editorKey, setEditorKey] = useState(0);
  const openEditor = () => { setEditorKey((k) => k + 1); setEditorOpen(true); };

  // Single-source sidebar (Audit Issue 15). RcmDashboard used to ship its
  // own hand-rolled navItems list that drifted from the rest of the
  // clinician app — QA finding, Iter-114. Replaced with the shared
  // builder so every clinician surface renders the same 22-item sidebar.
  const navItems = clinicianNavItems({ pathname: '/clinician/billing' });

  const filtered = statusFilter === 'all' ? claims : claims.filter((c) => c.status === statusFilter);
  const aging = summary?.ar_aging || { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  const agingMax = Math.max(1, ...Object.values(aging));

  return (
    <DashboardShell
      navItems={navItems}
      title="Billing & Revenue Cycle"
      subtitle="Capture charges, manage insurance claims, post payments, and track every dollar — minus our transparent service rate."
      testId="rcm-dashboard-page"
      rightSlot={
        <Button onClick={openEditor} className="cn-btn cn-btn-primary !py-2.5" data-testid="new-claim-btn">
          <Plus className="w-4 h-4" /> New claim
        </Button>
      }
    >
      {loading ? (
        <div className="grid sm:grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="cn-card h-28 animate-pulse bg-cn-bg/60" />)}</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rcm-kpis">
            <Kpi icon={Receipt} label="Total billed" value={money(summary?.total_billed)} sub={`${summary?.claim_count || 0} claims`} accent="pine" testId="kpi-billed" />
            <Kpi icon={TrendingUp} label="Collected" value={money(summary?.total_collected)} sub={`Net ${money(summary?.clinician_net)} after fees`} accent="sage" testId="kpi-collected" />
            <Kpi icon={Wallet} label="Outstanding A/R" value={money(summary?.total_outstanding)} sub="Awaiting payment" accent="terracotta" testId="kpi-outstanding" />
            <Kpi icon={AlertTriangle} label="Denied" value={summary?.denied_count || 0} sub={`Platform fees ${money(summary?.platform_fees)}`} accent="amber" testId="kpi-denied" />
          </div>

          {/* AR Aging */}
          <div className="cn-card p-6 mt-6" data-testid="rcm-ar-aging">
            <div className="flex items-center gap-2 mb-4"><Clock className="w-4 h-4 text-cn-primary" /><h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Accounts receivable aging</h3></div>
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(aging).map(([bucket, amt]) => (
                <div key={bucket}>
                  <div className="flex items-end justify-between"><span className="text-xs font-semibold text-cn-text-muted">{bucket} days</span><span className="font-display font-bold text-cn-text dark:text-white">{money(amt)}</span></div>
                  <div className="mt-1.5 h-2 rounded-full bg-cn-bone-200 overflow-hidden"><div className={`h-full rounded-full ${bucket === '90+' ? 'bg-red-400' : bucket === '61-90' ? 'bg-amber-400' : 'bg-cn-primary'}`} style={{ width: `${(amt / agingMax) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          </div>

          {/* Missed revenue — unbilled visit notes */}
          {unbilled.count > 0 && (
            <div className="cn-card p-6 mt-6 border-amber-300/70 bg-amber-50/40 dark:bg-amber-500/5" data-testid="rcm-unbilled-inbox">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"><Inbox className="w-4 h-4" /></span>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Missed revenue inbox</h3>
                    <p className="text-xs text-cn-text-muted">{unbilled.count} completed {unbilled.count === 1 ? 'visit' : 'visits'} documented but not yet billed</p>
                  </div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Est. recoverable</div>
                    <div className="font-display font-bold text-2xl text-amber-600" data-testid="unbilled-estimated-total">{money(unbilled.estimated_total)}</div>
                  </div>
                  <Button
                    onClick={bulkBillAll}
                    disabled={bulkBilling || unbilled.count === 0}
                    className="cn-btn cn-btn-primary !py-2.5 !px-4 text-xs shrink-0"
                    data-testid="unbilled-bulk-autobill-btn"
                    title="Auto-code every unbilled visit into a draft claim (you still review & submit)"
                  >
                    {bulkBilling
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Auto-billing…</>
                      : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Auto-bill all</>}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {unbilled.items.slice(0, 6).map((it) => (
                  <div key={it.note_id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-white dark:bg-white/5 border border-cn-border" data-testid={`unbilled-row-${it.note_id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-cn-text dark:text-white truncate">{it.patient_name} · <span className="font-normal text-cn-text-muted">{it.title}</span></div>
                      <div className="text-xs text-cn-text-muted flex items-center gap-2 mt-0.5">
                        <span>{it.date_of_service || '—'}</span>
                        {it.age_days != null && <span className={`${it.age_days >= 14 ? 'text-red-500 font-semibold' : ''}`}>· {it.age_days}d old</span>}
                        <span>· est. {money(it.estimated_amount)}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => codeUnbilled(it)}
                      disabled={codingNote === it.note_id}
                      className="cn-btn cn-btn-primary !py-2 !px-3 text-xs shrink-0"
                      data-testid={`unbilled-code-btn-${it.note_id}`}
                    >
                      {codingNote === it.note_id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Coding…</> : <><Sparkles className="w-3.5 h-3.5" /> Create claim</>}
                    </Button>
                  </div>
                ))}
                {unbilled.count > 6 && <p className="text-xs text-cn-text-muted pt-1">+ {unbilled.count - 6} more unbilled in Clinical Notes.</p>}
              </div>
            </div>
          )}

          {/* Filter + table */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Claims</h3>
            <div className="w-44">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 rounded-xl bg-white dark:bg-white/5" data-testid="claim-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(CLAIM_STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="cn-card p-12 text-center mt-3" data-testid="rcm-empty">
              <div className="w-16 h-16 rounded-3xl bg-cn-primary/10 text-cn-primary mx-auto flex items-center justify-center mb-4"><FileSpreadsheet className="w-8 h-8" /></div>
              <h3 className="cn-display text-xl text-cn-text dark:text-white">No claims yet</h3>
              <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">Create a claim to capture charges for a visit and start your revenue cycle.</p>
              <Button onClick={openEditor} className="cn-btn cn-btn-primary mt-5 mx-auto" data-testid="empty-new-claim-btn"><Plus className="w-4 h-4" /> New claim</Button>
            </div>
          ) : (
            <div className="cn-card mt-3 overflow-x-auto" data-testid="claims-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
                  <th className="p-4">Claim #</th><th className="p-4">Patient</th><th className="p-4">DOS</th><th className="p-4">Payer</th>
                  <th className="p-4 text-right">Charges</th><th className="p-4 text-right">Balance</th><th className="p-4">Status</th>
                </tr></thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} onClick={() => setDetail(c)} className="border-b border-cn-border/50 last:border-0 hover:bg-cn-bg/60 cursor-pointer" data-testid={`claim-row-${c.id}`}>
                      <td className="p-4 font-mono text-xs text-cn-primary">{c.claim_number}</td>
                      <td className="p-4 font-semibold text-cn-text dark:text-white">{c.patient_name}</td>
                      <td className="p-4 text-cn-text-muted">{c.date_of_service}</td>
                      <td className="p-4 text-cn-text-muted">{c.billing_type === 'self_pay' ? 'Self-pay' : (c.payer_name || '—')}</td>
                      <td className="p-4 text-right font-semibold">{money(c.total_charges)}</td>
                      <td className="p-4 text-right font-semibold text-cn-terracotta-600">{money(c.balance)}</td>
                      <td className="p-4"><div className="flex items-center gap-1.5"><StatusBadge status={c.status} />{c.status === 'draft' && (c.warnings || []).length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title={`${c.warnings.length} item(s) to fix before submitting`} data-testid={`claim-warning-flag-${c.id}`} />}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <ClaimEditorModal open={editorOpen} onOpenChange={setEditorOpen} codeSets={codeSets} eligible={eligible} getAuthHeader={getAuthHeader} onCreated={() => { setEditorOpen(false); load(); }} />
      <ClaimDetailModal key={detail?.id || 'none'} claim={detail} onOpenChange={(v) => !v && setDetail(null)} getAuthHeader={getAuthHeader} onChanged={(updated) => { setDetail(updated); load(); }} />
    </DashboardShell>
  );
}

/* ---------------- Claim editor (charge capture) ---------------- */
const emptyLine = () => ({ cpt_code: '', description: '', units: 1, charge: '' });

const ClaimEditorModal = ({ open, onOpenChange, codeSets, eligible, getAuthHeader, onCreated }) => {
  const [form, setForm] = useState({ patient_name: '', patient_dob: '', date_of_service: new Date().toISOString().slice(0, 10), place_of_service: '12', billing_type: 'insurance', payer_name: '', member_id: '', group_number: '', booking_id: '', patient_id: '', notes: '' });
  const [lines, setLines] = useState([emptyLine()]);
  const [dx, setDx] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const total = lines.reduce((s, l) => s + (Number(l.units || 0) * Number(l.charge || 0)), 0);

  const prefillBooking = (bid) => {
    const b = eligible.find((x) => x.booking_id === bid);
    if (b) setForm((p) => ({ ...p, booking_id: bid, patient_id: b.patient_id || '', patient_name: b.patient_name || p.patient_name, date_of_service: b.date_of_service || p.date_of_service }));
  };

  const save = async () => {
    const validLines = lines.filter((l) => l.cpt_code && Number(l.charge) >= 0 && Number(l.units) > 0);
    if (!form.patient_name.trim()) return toast.error('Patient name is required');
    if (validLines.length === 0) return toast.error('Add at least one service line with a CPT code');
    setSaving(true);
    try {
      const payload = {
        patient_name: form.patient_name, patient_dob: form.patient_dob || null,
        date_of_service: form.date_of_service, place_of_service: form.place_of_service,
        billing_type: form.billing_type,
        payer_name: form.billing_type === 'insurance' ? (form.payer_name || null) : null,
        member_id: form.billing_type === 'insurance' ? (form.member_id || null) : null,
        group_number: form.billing_type === 'insurance' ? (form.group_number || null) : null,
        booking_id: form.booking_id || null, patient_id: form.patient_id || null,
        diagnosis_codes: dx, notes: form.notes || null,
        service_lines: validLines.map((l) => ({ cpt_code: l.cpt_code, description: l.description || null, units: Number(l.units), charge: Number(l.charge) })),
      };
      await axios.post(`${API}/rcm/claims`, payload, getAuthHeader());
      toast.success('Claim created as a draft');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create claim');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="claim-editor-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-cn-text">New claim — charge capture</DialogTitle>
          <DialogDescription className="text-cn-text-muted">Capture the visit, codes, and charges. Saved as a draft you can submit when ready.</DialogDescription>
        </DialogHeader>

        {eligible.length > 0 && (
          <div className="mt-2">
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Start from a completed visit</Label>
            <Select value={form.booking_id || 'none'} onValueChange={(v) => prefillBooking(v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white dark:bg-white/5" data-testid="claim-prefill-booking"><SelectValue placeholder="Optional — pick a visit" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Manual entry</SelectItem>
                {eligible.map((b) => <SelectItem key={b.booking_id} value={b.booking_id}>{b.patient_name} · {b.date_of_service || 'recent'} · {money(b.amount)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Patient name</Label><Input value={form.patient_name} onChange={(e) => set('patient_name', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="claim-patient-name" /></div>
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Patient DOB</Label><Input type="date" value={form.patient_dob} onChange={(e) => set('patient_dob', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="claim-patient-dob" /></div>
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Date of service</Label><Input type="date" value={form.date_of_service} onChange={(e) => set('date_of_service', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="claim-dos" /></div>
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase">Place of service</Label>
            <Select value={form.place_of_service} onValueChange={(v) => set('place_of_service', v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white dark:bg-white/5" data-testid="claim-pos"><SelectValue /></SelectTrigger>
              <SelectContent>{(codeSets.place_of_service || []).map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase">Billing type</Label>
            <Select value={form.billing_type} onValueChange={(v) => set('billing_type', v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white dark:bg-white/5" data-testid="claim-billing-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="self_pay">Self-pay</SelectItem></SelectContent>
            </Select>
          </div>
          {form.billing_type === 'insurance' && (
            <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Payer</Label><Input value={form.payer_name} onChange={(e) => set('payer_name', e.target.value)} placeholder="e.g. Aetna" className="mt-1.5 h-11 rounded-xl" data-testid="claim-payer" /></div>
          )}
          {form.billing_type === 'insurance' && (
            <>
              <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Member ID</Label><Input value={form.member_id} onChange={(e) => set('member_id', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="claim-member-id" /></div>
              <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Group #</Label><Input value={form.group_number} onChange={(e) => set('group_number', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="claim-group" /></div>
            </>
          )}
        </div>

        {/* Diagnoses */}
        <div className="mt-4">
          <Label className="text-xs font-semibold text-cn-text-muted uppercase">Diagnoses (ICD-10)</Label>
          <Select value="" onValueChange={(code) => { const found = codeSets.icd10.find((d) => d.code === code); if (found && !dx.some((d) => d.code === code)) setDx((p) => [...p, found]); }}>
            <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-white dark:bg-white/5" data-testid="claim-dx-select"><SelectValue placeholder="Add a diagnosis code" /></SelectTrigger>
            <SelectContent className="max-h-64">{codeSets.icd10.map((d) => <SelectItem key={d.code} value={d.code}>{d.code} — {d.description}</SelectItem>)}</SelectContent>
          </Select>
          {dx.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {dx.map((d) => (
                <span key={d.code} className="inline-flex items-center gap-1.5 bg-cn-primary/10 text-cn-primary px-2.5 py-1 rounded-full text-xs font-semibold">
                  {d.code}<button type="button" onClick={() => setDx((p) => p.filter((x) => x.code !== d.code))} className="hover:text-cn-error"><XCircle className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Service lines */}
        <div className="mt-4">
          <div className="flex items-center justify-between"><Label className="text-xs font-semibold text-cn-text-muted uppercase">Service lines</Label><span className="text-sm font-semibold text-cn-text">Total: {money(total)}</span></div>
          <div className="space-y-2 mt-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`service-line-${i}`}>
                <div className="col-span-3">
                  <Select value={l.cpt_code || ''} onValueChange={(v) => { const f = codeSets.cpt.find((c) => c.code === v); setLine(i, 'cpt_code', v); if (f && !l.description) setLine(i, 'description', f.description); }}>
                    <SelectTrigger className="h-10 rounded-lg bg-white dark:bg-white/5 text-xs" data-testid={`line-cpt-${i}`}><SelectValue placeholder="CPT" /></SelectTrigger>
                    <SelectContent className="max-h-64">{codeSets.cpt.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.description}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} placeholder="Description" className="col-span-5 h-10 rounded-lg text-xs" data-testid={`line-desc-${i}`} />
                <Input type="number" min="0.25" step="0.25" value={l.units} onChange={(e) => setLine(i, 'units', e.target.value)} placeholder="Units" className="col-span-1 h-10 rounded-lg text-xs" data-testid={`line-units-${i}`} />
                <Input type="number" min="0" value={l.charge} onChange={(e) => setLine(i, 'charge', e.target.value)} placeholder="$ / unit" className="col-span-2 h-10 rounded-lg text-xs" data-testid={`line-charge-${i}`} />
                <button type="button" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} disabled={lines.length === 1} className="col-span-1 text-cn-text-muted hover:text-cn-error disabled:opacity-30" data-testid={`line-remove-${i}`}><Trash2 className="w-4 h-4 mx-auto" /></button>
              </div>
            ))}
          </div>
          <Button type="button" onClick={() => setLines((p) => [...p, emptyLine()])} className="cn-btn cn-btn-ghost !py-1.5 mt-2 text-xs" data-testid="add-service-line"><Plus className="w-3.5 h-3.5" /> Add line</Button>
        </div>

        <div className="mt-4"><Label className="text-xs font-semibold text-cn-text-muted uppercase">Notes</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} className="mt-1.5 rounded-xl" rows={2} data-testid="claim-notes" /></div>

        <div className="flex gap-2 pt-4">
          <Button onClick={() => onOpenChange(false)} className="cn-btn cn-btn-ghost flex-1" data-testid="claim-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="cn-btn cn-btn-primary flex-1" data-testid="claim-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Receipt className="w-4 h-4" /> Create claim</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Claim detail + actions ---------------- */
const ClaimDetailModal = ({ claim, onOpenChange, getAuthHeader, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState(claim?.last_audit || null);
  const [payOpen, setPayOpen] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [payKey, setPayKey] = useState(0);
  const [denyKey, setDenyKey] = useState(0);
  if (!claim) return null;

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(successMsg); onChanged(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };
  const isInsurance = claim.billing_type !== 'self_pay';
  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await axios.post(`${API}/rcm/claims/${claim.id}/audit`, {}, { ...getAuthHeader(), timeout: 90000 });
      setAudit(data);
      if (data.skipped === 'self_pay') toast.message('Self-pay claim — no medical-necessity audit needed.');
      else if (data.medical_necessity_gap) toast.error('Medical necessity gap found — review the healing suggestions below.');
      else toast.success('Documentation supports the billed codes.');
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Audit failed');
    } finally { setAuditing(false); }
  };
  const submit = async (override = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rcm/claims/${claim.id}/submit`, { override_audit: override }, { ...getAuthHeader(), timeout: 90000 });
      toast.success(override ? 'Claim submitted (audit overridden)' : 'Claim submitted');
      setAudit(data.last_audit || null);
      onChanged(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.rcm_audit_blocked) {
        setAudit(detail.audit);
        toast.error('Submission blocked — documentation gap. See healing suggestions.');
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Action failed');
      }
    } finally { setBusy(false); }
  };
  const setStatus = (status, extra = {}) => act(() => axios.post(`${API}/rcm/claims/${claim.id}/status`, { status, ...extra }, getAuthHeader()), `Marked ${status.replace('_', ' ')}`);

  const canSubmit = claim.status === 'draft';
  const canReview = claim.status === 'submitted';
  const canPay = !['draft', 'void', 'paid'].includes(claim.status);

  return (
    <Dialog open={!!claim} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="claim-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="font-display text-2xl text-cn-text">{claim.patient_name}</DialogTitle>
            <StatusBadge status={claim.status} />
          </div>
          <DialogDescription className="text-cn-text-muted font-mono text-xs">{claim.claim_number} · DOS {claim.date_of_service}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="cn-card p-3 bg-cn-bg/60"><p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Charges</p><p className="font-display font-bold text-lg">{money(claim.total_charges)}</p></div>
          <div className="cn-card p-3 bg-cn-bg/60"><p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Collected</p><p className="font-display font-bold text-lg text-cn-primary">{money(claim.collected)}</p></div>
          <div className="cn-card p-3 bg-cn-bg/60"><p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Balance</p><p className="font-display font-bold text-lg text-cn-terracotta-600">{money(claim.balance)}</p></div>
        </div>

        <div className="mt-4 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-cn-text-muted">Payer</span><span className="font-semibold">{claim.billing_type === 'self_pay' ? 'Self-pay' : (claim.payer_name || '—')}</span></div>
          <div className="flex justify-between"><span className="text-cn-text-muted">Insurance paid</span><span>{money(claim.insurance_paid)}</span></div>
          <div className="flex justify-between"><span className="text-cn-text-muted">Adjustments</span><span>{money(claim.adjustments)}</span></div>
          <div className="flex justify-between"><span className="text-cn-text-muted">Patient responsibility</span><span>{money(claim.patient_responsibility)}</span></div>
          <div className="flex justify-between"><span className="text-cn-text-muted">Patient paid</span><span>{money(claim.patient_paid)}</span></div>
          <div className="flex justify-between border-t border-cn-border pt-1 mt-1"><span className="text-cn-text-muted">Service rate</span><span>-{money(claim.platform_fee)}</span></div>
          <div className="flex justify-between font-bold text-cn-primary"><span>Your net</span><span>{money(claim.clinician_net)}</span></div>
        </div>

        {/* Service lines */}
        <div className="mt-4 cn-card p-3 bg-cn-bg/40">
          <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Service lines</p>
          {(claim.service_lines || []).map((l) => (
            <div key={l.id} className="flex justify-between text-sm py-1 border-b border-cn-border/40 last:border-0">
              <span><span className="font-mono text-cn-primary">{l.cpt_code}</span> {l.description}</span>
              <span className="text-cn-text-muted">{l.units} × {money(l.charge)} = <b className="text-cn-text">{money(l.line_total)}</b></span>
            </div>
          ))}
          {(claim.denial_reason) && <p className="text-xs text-red-600 mt-2"><b>Denial:</b> {claim.denial_reason}</p>}
        </div>

        {/* Diagnoses (ICD-10) */}
        {(claim.diagnosis_codes || []).length > 0 && (
          <div className="mt-3 cn-card p-3 bg-cn-bg/40" data-testid="claim-diagnoses">
            <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Diagnoses (ICD-10)</p>
            <div className="flex flex-wrap gap-1.5">
              {claim.diagnosis_codes.map((d) => (
                <span key={d.code} className="inline-flex items-center gap-1.5 bg-cn-primary/10 text-cn-primary px-2.5 py-1 rounded-full text-xs font-semibold" data-testid={`claim-dx-${d.code}`}>
                  <span className="font-mono">{d.code}</span>{d.description ? <span className="font-normal text-cn-text-muted">{d.description}</span> : null}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Draft data-quality warnings — fix before submitting to the payer */}
        {claim.status === 'draft' && (claim.warnings || []).length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="claim-warnings">
            <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Fix before submitting</div>
            <ul className="mt-1.5 space-y-0.5 text-xs text-amber-800">
              {(claim.warnings || []).map((w, i) => (
                <li key={i} className="flex items-start gap-1.5" data-testid={`claim-warning-${i}`}><span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 shrink-0" />{w}</li>
              ))}
            </ul>
          </div>
        )}

        {/* AI pre-submission clinical audit — medical-necessity gap + healing suggestions */}
        {audit && audit.skipped !== 'self_pay' && (
          <div
            className={`mt-4 rounded-xl border p-3 ${audit.medical_necessity_gap ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}
            data-testid="claim-audit-panel"
          >
            <div className="flex items-center justify-between gap-2">
              <div className={`flex items-center gap-2 font-semibold text-sm ${audit.medical_necessity_gap ? 'text-red-700' : 'text-emerald-700'}`}>
                {audit.medical_necessity_gap ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                {audit.medical_necessity_gap ? 'Medical necessity gap' : 'Documentation supports codes'}
              </div>
              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${
                audit.risk_level === 'high' ? 'bg-red-100 text-red-700 border-red-300'
                  : audit.risk_level === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-300'
                    : 'bg-emerald-100 text-emerald-700 border-emerald-300'}`} data-testid="claim-audit-risk">{audit.risk_level} risk</span>
            </div>
            {audit.summary && <p className="mt-1.5 text-xs text-cn-text-muted" data-testid="claim-audit-summary">{audit.summary}</p>}
            {audit.note_found === false && <p className="mt-1 text-xs text-red-700 font-medium">No SOAP note was found for this visit.</p>}
            {(audit.findings || []).filter((f) => !f.supported).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Unsupported codes</p>
                <ul className="mt-1 space-y-0.5 text-xs text-cn-text">
                  {(audit.findings || []).filter((f) => !f.supported).map((f, i) => (
                    <li key={i} data-testid={`claim-audit-finding-${i}`}><span className="font-mono text-cn-primary">{f.cpt_code}</span> — {f.issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {(audit.healing_suggestions || []).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted flex items-center gap-1"><Sparkles className="w-3 h-3 text-cn-primary" /> Healing suggestions</p>
                <ul className="mt-1 space-y-0.5 text-xs text-cn-text">
                  {(audit.healing_suggestions || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-1.5" data-testid={`claim-healing-${i}`}><span className="mt-1.5 w-1 h-1 rounded-full bg-cn-primary shrink-0" />{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {audit.ai_unavailable && <p className="mt-1.5 text-[10px] text-cn-text-muted">AI audit was unavailable; the claim was not auto-blocked.</p>}
            {audit.overridden && <p className="mt-1.5 text-[10px] text-amber-700 font-medium">Submitted with clinician override.</p>}
          </div>
        )}

        {/* EDI 837P transmission info (insurance claims) */}
        {claim.transmission_info?.status === 'transmitted' && (
          <div className="mt-4 rounded-xl border border-cn-border bg-cn-bg/50 p-3" data-testid="claim-transmission">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-cn-text-muted"><Building2 className="w-3.5 h-3.5 text-cn-primary" /> EDI 837P transmission</span>
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">Simulated</span>
            </div>
            <div className="mt-1.5 text-xs text-cn-text-muted space-y-0.5">
              <div className="flex justify-between"><span>Clearinghouse</span><span className="font-semibold text-cn-text dark:text-white">{claim.transmission_info.clearinghouse}</span></div>
              <div className="flex justify-between"><span>Transmission ID</span><span className="font-mono">{(claim.transmission_info.transmission_id || '').slice(0, 8)}</span></div>
            </div>
            {claim.transmission_info.edi_preview && (
              <pre className="mt-2 text-[10px] leading-relaxed font-mono bg-slate-900 text-slate-100 rounded-lg p-2.5 overflow-x-auto max-h-32" data-testid="claim-edi-preview">{claim.transmission_info.edi_preview}</pre>
            )}
            <p className="mt-1.5 text-[10px] text-cn-text-muted">Demo only — no real clearinghouse transmission yet.</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-4">
          {canSubmit && isInsurance && <Button onClick={runAudit} disabled={auditing || busy} className="cn-btn cn-btn-ghost !py-2" data-testid="claim-audit-btn">{auditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Run AI audit</Button>}
          {canSubmit && <Button onClick={() => submit(false)} disabled={busy || auditing} className="cn-btn cn-btn-primary !py-2" data-testid="claim-submit-btn"><Send className="w-4 h-4" /> Submit claim</Button>}
          {canSubmit && audit?.medical_necessity_gap && <Button onClick={() => submit(true)} disabled={busy} className="cn-btn cn-btn-ghost !py-2 text-amber-700" data-testid="claim-submit-override-btn"><Send className="w-4 h-4" /> Submit anyway</Button>}
          {canReview && <Button onClick={() => setStatus('in_review')} disabled={busy} className="cn-btn cn-btn-ghost !py-2" data-testid="claim-review-btn"><Clock className="w-4 h-4" /> Mark in review</Button>}
          {canPay && <Button onClick={() => { setPayKey((k) => k + 1); setPayOpen(true); }} disabled={busy} className="cn-btn cn-btn-accent !py-2" data-testid="claim-post-payment-btn"><CreditCard className="w-4 h-4" /> Post payment</Button>}
          {canPay && claim.status !== 'denied' && <Button onClick={() => { setDenyKey((k) => k + 1); setDenyOpen(true); }} disabled={busy} className="cn-btn cn-btn-ghost !py-2 text-red-600" data-testid="claim-deny-btn"><XCircle className="w-4 h-4" /> Mark denied</Button>}
          {claim.status === 'denied' && <Button onClick={() => setStatus('appealed')} disabled={busy} className="cn-btn cn-btn-ghost !py-2" data-testid="claim-appeal-btn"><Send className="w-4 h-4" /> Appeal</Button>}
          <Button onClick={() => openSuperbill(claim)} className="cn-btn cn-btn-ghost !py-2" data-testid="claim-superbill-btn"><Printer className="w-4 h-4" /> Superbill</Button>
        </div>

        <PaymentModal key={payKey} open={payOpen} onOpenChange={setPayOpen} claim={claim} getAuthHeader={getAuthHeader} onPosted={(d) => { setPayOpen(false); onChanged(d); }} />
        <DenyModal key={denyKey} open={denyOpen} onOpenChange={setDenyOpen} onConfirm={(reason, code) => { setDenyOpen(false); setStatus('denied', { denial_reason: reason, denial_code: code }); }} />
      </DialogContent>
    </Dialog>
  );
};

const PaymentModal = ({ open, onOpenChange, claim, getAuthHeader, onPosted }) => {
  const [type, setType] = useState('insurance');
  const [amount, setAmount] = useState('');
  const [adjustment, setAdjustment] = useState('');
  const [patientResp, setPatientResp] = useState('');
  const [method, setMethod] = useState('');
  const [busy, setBusy] = useState(false);

  const post = async () => {
    if (!(Number(amount) >= 0)) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rcm/claims/${claim.id}/payments`, {
        payment_type: type, amount: Number(amount || 0),
        adjustment: type === 'insurance' ? Number(adjustment || 0) : 0,
        patient_responsibility: type === 'insurance' && patientResp !== '' ? Number(patientResp) : null,
        method: method || null,
      }, getAuthHeader());
      toast.success('Payment posted'); onPosted(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not post payment'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="payment-modal">
        <DialogHeader><DialogTitle className="font-display text-xl text-cn-text">Post a payment</DialogTitle><DialogDescription className="text-cn-text-muted">Record an insurance remittance or a patient payment.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase">Payment type</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="payment-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="insurance">Insurance remittance</SelectItem><SelectItem value="patient">Patient payment</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Amount paid ($)</Label><Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="payment-amount" /></div>
          {type === 'insurance' && <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Contractual adjustment ($)</Label><Input type="number" min="0" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="payment-adjustment" /></div>}
          {type === 'insurance' && <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Patient responsibility ($)</Label><Input type="number" min="0" value={patientResp} onChange={(e) => setPatientResp(e.target.value)} placeholder="copay + coinsurance + deductible" className="mt-1.5 h-11 rounded-xl" data-testid="payment-patient-resp" /></div>}
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Method / reference</Label><Input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="ERA, EFT, check #…" className="mt-1.5 h-11 rounded-xl" data-testid="payment-method" /></div>
          <div className="flex gap-2 pt-2"><Button onClick={() => onOpenChange(false)} className="cn-btn cn-btn-ghost flex-1">Cancel</Button><Button onClick={post} disabled={busy} className="cn-btn cn-btn-primary flex-1" data-testid="payment-post-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Post</>}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DenyModal = ({ open, onOpenChange, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="deny-modal">
        <DialogHeader><DialogTitle className="font-display text-xl text-cn-text">Mark claim denied</DialogTitle><DialogDescription className="text-cn-text-muted">Record the denial so you can work it / appeal.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Denial reason</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 rounded-xl" rows={3} data-testid="deny-reason" /></div>
          <div><Label className="text-xs font-semibold text-cn-text-muted uppercase">Denial code (CARC)</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. CO-97" className="mt-1.5 h-11 rounded-xl" data-testid="deny-code" /></div>
          <div className="flex gap-2 pt-2"><Button onClick={() => onOpenChange(false)} className="cn-btn cn-btn-ghost flex-1">Cancel</Button><Button onClick={() => onConfirm(reason || null, code || null)} className="cn-btn cn-btn-primary flex-1 !bg-red-600" data-testid="deny-confirm">Confirm denial</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
